import { ServiceCase } from "../../models/serviceCase";
import { Inject } from "@fiqas/angular-utils";
import { ServiceCaseEdit } from "./serviceCaseEdit";
import { UserService } from "../../services/fiqassistApi/userService";
import { Application } from "../application";
import { User } from "../../models/user";
import { CompactPerson } from "../../models/person";
import { LogService } from "../../services/logService";

export class ManageWatchers<T extends ServiceCase> {
    static controllerId = 'ManageWatchersComponent';
    private searchFilter: string;
    private isLoading: boolean;

    public serviceCaseStatus: string;
    public serviceCaseDisabled: boolean;

    constructor(
        @Inject('$uibModalInstance') private $modalInstance: angular.ui.bootstrap.IModalServiceInstance,
        @Inject('page') public page: ServiceCaseEdit<T>,
        @Inject('enableWatch') private enableWatch: boolean,
        @Inject() private userService: UserService,
        @Inject() private logService: LogService,
        @Inject('application') private application: Application,
        @Inject('serviceCase') private serviceCase: () => T
    ) {
        $modalInstance.opened.then(() => {
            this.loadLists();
            this.application.user.then(u => this.user = u);
        });
        this.serviceCaseStatus = this.serviceCase().status;
        this.serviceCaseDisabled = (this.serviceCaseStatus === 'closed' || this.serviceCaseStatus === 'cancelled');
    }
    private persons: (CompactPerson & { selected: boolean })[];
    private watchers: (CompactPerson & { selected: boolean })[];
    private user: User;

    public get personList() {
        return this.persons ?
            this.persons
                .filter(p => ((p.isFiqasEmployee && this.user.isFiqasEmployee) || !p.isFiqasEmployee) && !p.isFormerEmployee)
                .filter(p => !this.searchFilter || p.name.toLowerCase().indexOf(this.searchFilter.toLowerCase()) > -1)
                .sort((a, b) => a.name > b.name ? 1 : -1)
            : undefined;
    }

    public get watchersList() {
        return this.watchers ?
            this.watchers
            : undefined;
    }

    private onSearchValueChanged(value: string) {
        this.searchFilter = value;
    }

    private async loadLists() {
        try {
            this.watchers = [];
            if (this.serviceCase().actionLinks.generalLinks.getWatchers) {
                var watchers = await this.serviceCase().actionLinks.generalLinks.getWatchers.action();
                this.watchers = watchers.items
                    .sort((a, b) => a.name.trim() > b.name.trim() ? 1 : -1)
                    .map(p => {
                        return <CompactPerson & { selected: boolean }>{
                            id: p.id,
                            name: p.name.trim(),
                            isFiqasEmployee: p.isFiqasEmployee,
                            isFormerEmployee: p.isFormerEmployee,
                            selected: false
                        }
                    });
                this.serviceCase().watcherCount = this.watchers.filter(x => !x.isFiqasEmployee).length;
            }
            var persons = await this.userService.getPersons([this.serviceCase().customer.ref]);
            this.persons = persons.items
                .filter(p => p.hasLogin && !this.watchers.some(w => w.id.ref == p.id.ref))
                .sort((a, b) => a.name.trim() > b.name.trim() ? 1 : -1)
                .map(p => {
                    return <CompactPerson & { selected: boolean }>{
                        id: p.id,
                        name: p.name.trim(),
                        isFiqasEmployee: p.isFiqasEmployee,
                        isFormerEmployee: p.isFormerEmployee,
                        selected: false
                    }
                });
        } catch (e) {
            this.logService.error('ManageWatchers', 'Error loading data', e);
        } finally {

        }

    }

    public Close() {
        this.$modalInstance.close();
    }

    public get selectedPersons() {
        return this.personList ? this.personList.filter(p => p.selected).map(p => p.id.ref) : undefined;
    }

    public async addWatcher(ref: string = null) {
        var persons = ref ? [ref] : this.selectedPersons;
        if (this.enableWatch && persons && persons.length > 0) {
            this.persons = this.persons.filter(p => !persons.some(pp => pp == p.id.ref));
            try {
                this.isLoading = true;
                await this.serviceCase().actionLinks.generalLinks.addWatchers.action(null, { "personId": persons })
                await this.loadLists();
            } catch (e) {
                this.logService.error('ManageWatchers', 'Error loading data', e);
            } finally {
                this.isLoading = false;
            }
        }
    }

    public get selectedWatchers() {
        return this.watchers ? this.watchers.filter(p => p.selected).map(p => p.id.ref) : undefined;
    }

    public async removeWatcher(ref: string = null) {
        var watchers = ref ? [ref] : this.selectedWatchers;
        if (this.enableWatch && watchers && watchers.length > 0) {
            this.watchers = this.watchers.filter(w => !watchers.some(wi => wi == w.id.ref));
            try {
                this.isLoading = true;
                await this.serviceCase().actionLinks.generalLinks.removeWatchers.action(null, { "personId": watchers })
                await this.loadLists();
            } catch (e) {
                this.logService.error('ManageWatchers', 'Error loading data', e);
            } finally {
                this.isLoading = false;
            }
        }
    }

    private personKeypress(event: JQueryEventObject) {
        var keycode = event.keyCode;
        if ((event.ctrlKey || event.metaKey) && event.key == "a") {
            this.clearWatcherSelection();
            this.personList.forEach(p => p.selected = true);
            event.preventDefault();
        }
    }

    private personClicked(event: JQueryEventObject, person: CompactPerson) {
        if (event.ctrlKey || event.metaKey) {
            this.clearWatcherSelection();
            this.personList.filter(p => p.id.equals(person.id)).forEach(p => p.selected = !p.selected);
        } else if (event.shiftKey) {
            this.clearWatcherSelection();
            var persons = this.personList;
            var first = persons.indexOf(persons.filter(p => p.selected).shift());
            var last = persons.lastIndexOf(persons.filter(p => p.selected).shift());
            var pi = persons.indexOf(persons.filter(p => p.id.equals(person.id)).shift());
            persons.forEach((p, i) => {
                if (pi >= first)
                    p.selected = i >= first && i <= pi;
                else
                    p.selected = i <= first && i >= pi;
            });
        } else {
            this.clearWatcherSelection();
            this.personList.forEach(p => p.selected = p.id.equals(person.id));
        }
        event.preventDefault();
        event.stopPropagation();
        return false;
    }

    private clearPersonSelection() {
        this.personList.forEach((p) => {
                p.selected = false;
        });
    }

    private clearWatcherSelection() {
        this.watchersList.forEach((w) => {
                w.selected = false;
        });
    }

    private watcherKeypress(event: JQueryEventObject) {
        var keycode = event.keyCode;
        if ((event.ctrlKey || event.metaKey) && event.key == "a") {
            this.clearPersonSelection();
            this.watchersList.forEach(p => p.selected = true);
            event.preventDefault();
        }
    }

    private watcherClicked(event: JQueryEventObject, watcher: CompactPerson) {
        if (event.ctrlKey || event.metaKey) {
            this.clearPersonSelection();
            this.watchersList.filter(p => p.id.equals(watcher.id)).forEach(p => p.selected = !p.selected);
        } else if (event.shiftKey) {
            this.clearPersonSelection();
            var persons = this.watchers;
            var first = persons.indexOf(persons.filter(p => p.selected).shift());
            var last = persons.lastIndexOf(persons.filter(p => p.selected).shift());
            var pi = persons.indexOf(persons.filter(p => p.id.equals(watcher.id)).shift());
            persons.forEach((p, i) => {
                if (pi >= first)
                    p.selected = i >= first && i <= pi;
                else
                    p.selected = i <= first && i >= pi;
            });
        } else {
            this.clearPersonSelection();
            this.watchersList.forEach(p => p.selected = p.id.equals(watcher.id));
        }
        event.preventDefault();
        event.stopPropagation();
        return false;
    }
}
